import { FieldValidator } from "final-form";
import { useState } from "react";
import CTFieldValidator, {
  name,
  email,
  required,
  strongPassword,
  strongPasswordForCA,
} from "@Components/forms/helpers/FieldValidator";
import { isCanadianLocale } from "@Utils/locale";
import {
  IForgotPassword,
  ILocaleDetail,
  ILogin,
  ISignup,
} from "@wff/interfaces";
import { IStringValidator } from "@wff/interfaces/validators";
import { useAppSelector } from "@wff/store/hooks";

export const initialForgotPasswordState: IForgotPassword = {
  email: "",
};

export const initialCheckoutLoginState: ILogin = {
  ...initialForgotPasswordState,
  password: "",
};

export const defaultCheckoutSignupState = {
  ...initialCheckoutLoginState,
  firstName: "",
  lastName: "",
  confirmedPassword: "",
  acceptMarketing: true,
};

export const initialCheckoutSignupState = (locale: ILocaleDetail): ISignup => {
  const defaultState = { ...defaultCheckoutSignupState };
  defaultState.acceptMarketing = !isCanadianLocale(locale) ? true : false;
  return defaultState;
};

export interface IAuthFormValidators {
  [key: string]: IStringValidator;
}

export type TAuthStateHandler = (key: string, value: any) => void;

const authFormValidator = (
  value: string,
  fieldValidator: FieldValidator<string>
) => {
  const validator = fieldValidator;
  const validatorResult = validator(value);
  return validatorResult === undefined;
};

const ifErrorMsgThenRenderIt = (
  errors: { [key: string]: any } | undefined,
  key: string
) => {
  return errors && Object.keys(errors).length ? errors[key] : undefined;
};

// helper function for consolidating the login/signup validation logic into a single place
export const authFormFieldValidatorSwitch = (
  key: string,
  errorMessages?: { [key: string]: any },
  languageCode?: string
): FieldValidator<string> => {
  let validator;
  switch (key) {
    case "email":
      validator = CTFieldValidator(
        [
          required(ifErrorMsgThenRenderIt(errorMessages, "required")),
          email(ifErrorMsgThenRenderIt(errorMessages, "email")),
        ],
        "Email"
      );
      break;
    case "password":
      validator = CTFieldValidator(
        [
          required(ifErrorMsgThenRenderIt(errorMessages, "required")),
          languageCode === "en-CA" || languageCode === "fr-CA"
            ? strongPasswordForCA(
                ifErrorMsgThenRenderIt(errorMessages, "password")
              )
            : strongPassword(ifErrorMsgThenRenderIt(errorMessages, "password")),
        ],
        "Password"
      );
      break;
    case "firstName":
      validator = CTFieldValidator(
        [required(ifErrorMsgThenRenderIt(errorMessages, "required")), name()],
        "First Name"
      );
      break;
    case "lastName":
      validator = CTFieldValidator(
        [required(ifErrorMsgThenRenderIt(errorMessages, "required")), name()],
        "Last Name"
      );
      break;
    default:
      validator = CTFieldValidator([required()]);
      break;
  }
  return validator;
};

export const authFormValidators: IAuthFormValidators = {
  email: (value: string) => {
    return authFormValidator(value, authFormFieldValidatorSwitch("email"));
  },
  password: (value: string) => {
    return authFormValidator(value, authFormFieldValidatorSwitch("password"));
  },
  firstName: (value: string) => {
    return authFormValidator(value, authFormFieldValidatorSwitch("firstName"));
  },
  lastName: (value: string) => {
    return authFormValidator(value, authFormFieldValidatorSwitch("lastName"));
  },
};

export const useForgotPassword = () => {
  const [forgotPasswordState, setForgotPasswordState] = useState(
    initialForgotPasswordState
  );

  const forgotPasswordStateHandler: TAuthStateHandler = (key, value) => {
    setForgotPasswordState({ ...forgotPasswordState, [key]: value });
  };

  return {
    forgotPasswordState,
    setForgotPasswordState,
    forgotPasswordStateHandler,
  };
};

export const useLogin = () => {
  const [loginState, setLoginState] = useState<ILogin>(
    initialCheckoutLoginState
  );

  const loginStateHandler: TAuthStateHandler = (key, value) => {
    setLoginState({ ...loginState, [key]: value });
  };

  return {
    loginState,
    setLoginState,
    loginStateHandler,
  };
};

export const useSignup = (initialState?: Partial<ISignup>) => {
  const { locale } = useAppSelector((state) => state);
  const [signupState, setSignupState] = useState<ISignup>({
    ...initialCheckoutSignupState(locale),
    ...initialState,
  });

  const signupStateHandler: TAuthStateHandler = (key, value) => {
    setSignupState({ ...signupState, [key]: value });
  };

  return {
    signupState,
    setSignupState,
    signupStateHandler,
  };
};
